import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCounter } from "react-use";
import { EditTool } from "../EditTool";
import { ShapeManagerState } from "./useShapeManager";
import { Facility, Device, Spot, LaneLine } from "@app/shared";

export enum EditMode {
    None,
    Geometry,
    Layout,
}

interface UseToolManagerProps {
    facility: Facility;
    shapeManager: ShapeManagerState;
    defaultDeviceId?: string;
}

export interface ToolManagerState {
    editMode: EditMode;
    currentTool: EditTool<any> | undefined;
    isToolLocked: boolean;

    setEditMode: (mode: EditMode) => void;
    selectTool: (tool: EditTool<any>) => void;
    lockTool: () => void;
    unlockTool: () => void;
    clearTool: () => void;
    createShapeWithTool: (tool: EditTool<any>) => void;
    reset: () => void;

    getNextTempId: () => number;
    canContinueWithTool: () => boolean;
}

export function useToolManager({ facility, shapeManager, defaultDeviceId }: UseToolManagerProps): ToolManagerState {
    const [editMode, setEditModeInternal] = useState<EditMode>(EditMode.None);
    const [currentTool, setCurrentTool] = useState<EditTool<any> | undefined>();
    const [isToolLocked, setIsToolLocked] = useState(false);

    const setEditMode = useCallback((mode: EditMode) => {
        setEditModeInternal(mode);
    }, []);

    const initialMaxId = useMemo(() => {
        const initShapes = shapeManager.configShapes
            .filter((s) => s.$type !== Device.$type && s.$type !== Spot.$type)
            .map((sl) => {
                const val = parseInt(sl.name?.split(" ")?.pop() ?? "0");
                return Number.isNaN(val) ? 0 : val;
            });
        return Math.max(...initShapes, 1);
    }, [shapeManager.configShapes]);

    const maxIdRef = useRef<number>(initialMaxId);

    const [, { inc: incTempId, get: getTempId, reset: resetTempId }] = useCounter(initialMaxId + 1);

    useEffect(() => {
        maxIdRef.current = initialMaxId;
    }, [initialMaxId]);

    useEffect(() => {
        const shapes = shapeManager.configShapes
            .filter((s) => s.$type !== Device.$type && s.$type !== Spot.$type)
            .map((sl) => {
                const val = parseInt(sl.name?.split(" ")?.pop() ?? "0");
                return Number.isNaN(val) ? 0 : val;
            });
        const max = Math.max(...shapes, 1);

        if (max > maxIdRef.current) {
            maxIdRef.current = max;
            resetTempId(max + 1);
        }
    }, [facility.id, shapeManager.configShapes]);

    const getNextTempId = useCallback(() => {
        const id = getTempId();
        incTempId();
        if (id > maxIdRef.current) {
            maxIdRef.current = id;
        }

        return id;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const selectTool = useCallback(
        (tool: EditTool<any>) => {
            if (currentTool === tool && isToolLocked) {
                setIsToolLocked(false);
                setCurrentTool(undefined);
                shapeManager.removeUnstartedShapes();
            } else if (currentTool === tool) {
                setIsToolLocked(true);
            } else {
                setIsToolLocked(false);

                const newShape = tool.createShape(getNextTempId());
                if (newShape.$type === LaneLine.$type && defaultDeviceId) {
                    (newShape as LaneLine).deviceId = defaultDeviceId;
                }
                shapeManager.addShape(newShape);
                setCurrentTool(tool);
            }
        },
        [currentTool, isToolLocked, shapeManager, getNextTempId, defaultDeviceId],
    );

    const createShapeWithTool = useCallback(
        (tool: EditTool<any>) => {
            const newShape = tool.createShape(getNextTempId());
            if (newShape.$type === LaneLine.$type && defaultDeviceId) {
                (newShape as LaneLine).deviceId = defaultDeviceId;
            }
            shapeManager.addShape(newShape);
            setCurrentTool(tool);
        },
        [getNextTempId, shapeManager, defaultDeviceId],
    );

    const lockTool = useCallback(() => {
        setIsToolLocked(true);
    }, []);

    const unlockTool = useCallback(() => {
        setIsToolLocked(false);
    }, []);

    const clearTool = useCallback(() => {
        setCurrentTool(undefined);
        setIsToolLocked(false);
        shapeManager.removeUnstartedShapes();
    }, [shapeManager]);

    const reset = useCallback(() => {
        setEditModeInternal(EditMode.None);
        setCurrentTool(undefined);
        setIsToolLocked(false);
        shapeManager.resetToFacilityState();
    }, [shapeManager]);

    const canContinueWithTool = useCallback(() => {
        return isToolLocked && currentTool !== undefined;
    }, [isToolLocked, currentTool]);

    useEffect(() => {
        if (editMode !== EditMode.Layout) {
            clearTool();
        }
    }, [editMode]);

    return {
        editMode,
        currentTool,
        isToolLocked,
        setEditMode,
        selectTool,
        lockTool,
        unlockTool,
        clearTool,
        createShapeWithTool,
        reset,
        getNextTempId,
        canContinueWithTool,
    };
}
